import * as React from "react";
import { isEqual } from "lodash";
import { BeatLoader } from "react-spinners";
import LatLngBounds from "google-map-react/lib/utils/lib_geo/lat_lng_bounds.js";
import LatLng from "google-map-react/lib/utils/lib_geo/lat_lng.js";
import ReactMapboxGl, { Layer, Feature } from "react-mapbox-gl";
import { toastr } from "react-redux-toastr";
import { ICaseCenter } from "src/share/services/cases";
import { IAuthState, IToken } from "src/modules/Auth/store/types";
import environmentHelper from "src/share/utils/environmentHelper";
import { layerPaint } from "./layerPaint";
import { CaseTypes } from "src/share/types/case/case";
import { StatisticsService } from "src/share/services/statistics";
import { IMapCenter } from "src/share/types/common";

const Map = ReactMapboxGl({
  accessToken: environmentHelper.getReactMapboxGlConfig(),
});

type Props = {
  agencyCode: string;
  state: string;
  caseType?: CaseTypes;
  status?: "ALL" | "open" | "closed";
  range?: { lte: number; gte: number };
  auth?: IAuthState;
  markerCenter?: IMapCenter;
  onStyleLoad?: (map: any) => any;
};

type State = {
  bounds: any;
  casesCenter: ICaseCenter[];
  center: [number, number];
  loading: boolean;
  zoom: [number];
};

const defaultProps = {
  bounds: [
    [176.8834586, 64.0377778],
    [-161.7558334, -39.6714177],
  ],
  center: [0.5375194, 50.8437787],
  zoom: [1],
};

export class Heatmap extends React.PureComponent<Props, State> {
  public interval;
  constructor(props: Props) {
    super(props);
    this.state = {
      casesCenter: [],
      center: defaultProps.center as [number, number],
      bounds: undefined,
      loading: true,
      zoom: defaultProps.zoom as [number],
    };
  }
  public componentDidMount() {
    if (this.props.auth) {
      const idToken = this.props.auth.idToken as IToken;
      const agencyCode = idToken.payload["custom:agencyCode"];
      this.loadCasesCenter(agencyCode);
    }
  }
  public componentDidUpdate(prevProps: Props) {
    if (
      this.props.agencyCode !== prevProps.agencyCode ||
      this.props.state !== prevProps.state ||
      this.props.status !== prevProps.status ||
      this.props.caseType !== prevProps.caseType ||
      !isEqual(this.props.range, prevProps.range)
    ) {
      this.loadCasesCenter(this.props.agencyCode);
    }
  }
  public async loadCasesCenter(agencyCode: string) {
    try {
      this.setState({ loading: true });
      const casesCenter = await StatisticsService.getCasesCenter(
        agencyCode,
        this.props.status,
        this.props.caseType,
        this.props.state,
        this.props.range
      );
      if (casesCenter.length > 0) {
        const bounds = this.getCenterBounds(casesCenter);
        this.setState({ bounds, casesCenter, loading: false });
      } else {
        this.setState({
          bounds: defaultProps.bounds,
          casesCenter: [],
          loading: false,
          zoom: defaultProps.zoom as [number],
        });
      }
    } catch (e) {
      toastr.error("Error", "There was a problem loading the heatmap cases");
    }
  }
  private onStyleLoad = (map: any) => {
    const { onStyleLoad } = this.props;
    return onStyleLoad && onStyleLoad(map);
  };
  public getMapBounds = (points: ICaseCenter[]) => {
    const bounds = new LatLngBounds();
    const locations = points.map((point) => ({
      latitude: point.center[0],
      longitude: point.center[1],
    }));
    locations.forEach((location) => {
      bounds.extend(new LatLng(location.latitude, location.longitude));
    });
    return bounds;
  };
  public getCenterBounds(points: ICaseCenter[]) {
    const { _ne: ne, _sw: sw, _nw: nw, _se: se } = this.getMapBounds(points);
    return ne && sw
      ? [
          [ne.lat, ne.lng],
          [sw.lat, sw.lng],
        ]
      : nw && se
      ? [
          [nw.lat, nw.lng],
          [se.lat, se.lng],
        ]
      : undefined;
  }
  public render() {
    const { bounds, casesCenter, loading, zoom } = this.state;
    const { agencyCode } = this.props;
    return (
      <div id="heatmap" className="map">
        <Map
          style={"mapbox://styles/mapbox/light-v10"}
          center={this.state.center}
          containerStyle={{
            height: "calc(60vh - 78px)",
          }}
          fitBounds={bounds}
          zoom={zoom}
          fitBoundsOptions={{
            padding: agencyCode === "ALL" ? 0 : 100,
            maxZoom: 9,
          }}
          onStyleLoad={this.onStyleLoad}
        >
          <React.Fragment>
            <Layer
              type="circle"
              id="map-marker"
              paint={{
                "circle-radius": 5,
                "circle-color": "#C81414",
                "circle-stroke-opacity": 0.4,
                "circle-stroke-color": "#C81414",
                "circle-stroke-width": 4,
              }}
            >
              {this.props.markerCenter && (
                <Feature
                  coordinates={[
                    this.props.markerCenter.lng,
                    this.props.markerCenter.lat,
                  ]}
                />
              )}
            </Layer>
            <Layer type="heatmap" paint={layerPaint} before={"map-marker"}>
              {casesCenter.map((caseData: any, index: number) => (
                <Feature
                  key={index}
                  coordinates={caseData.center}
                  properties={caseData}
                />
              ))}
            </Layer>
            {loading && (
              <div className="help-loader">
                <BeatLoader loading={this.state.loading} />
              </div>
            )}
          </React.Fragment>
        </Map>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/share/components/Maps/Heatmap/Heatmap.tsx